import React, { useMemo, useState } from 'react';
import styled from 'astroturf/react';
import { Body } from 'js/design_system/Body';
import { Heading } from 'js/design_system/Heading';
import { Icon } from 'js/design_system/Icon';
import { Modal } from 'js/design_system/Modal';
import { Mono } from 'js/design_system/Mono';
import { ButtonLink } from 'js/design_system/link/ButtonLink';
import { ImpressionWatcher } from 'js/utils/impressions/ImpressionWatcher';
import { ImpressionEntity } from 'js/utils/impressions/watchImpressions';
import { imageUrl } from 'js/utils/image_url';
import { recordClickthrough, recordDismissal } from 'js/utils/dismissibles';
import { trackClick } from 'js/utils/trackClick';
import { highlightText } from 'js/mentions_modal/highlightText';
import { SOURCES, trackingExtra } from 'js/mentions_modal/tracking';
import {
  CitationExcerpt,
  SingularMentionCitationModalProps,
} from 'js/mentions_modal/types';

/**
 * Name of the Dismissibles::SingularMentionCitationModal record. The show is
 * recorded server-side when the interruption is built, so this client only ever
 * records the dismissal and the clickthrough.
 */
const DISMISSIBLE_NAME = 'SingularMentionCitationModal';

const CARD_RADIUS = '12px';
const CARD_BACKGROUND = '#050505';
const CONTENT_WIDTH = '400px';
const PLANET_IMAGE = imageUrl('mentions_modal/planet.png');

const ModalDialog = styled(Modal)`
  @import 'stylesheets/design_system/breakpoint';

  & :global(.ds2-5-modal__container) {
    width: 100%;
    padding: 0;
    background: ${CARD_BACKGROUND};
  }

  &::backdrop {
    background: rgba(20, 20, 19, 0.6);
  }

  @include breakpoint(medium, large) {
    &:global(.ds2-5-modal) {
      align-items: center;
      padding: var(--spacing-sm-24, 24px);
    }

    & :global(.ds2-5-modal__container) {
      max-width: 564px;
      border-radius: ${CARD_RADIUS};
    }
  }

  @include breakpoint(small) {
    &:global(.ds2-5-modal) {
      align-items: center;
      justify-content: center;
      padding: var(--spacing-sm-24, 24px);
    }

    & :global(.ds2-5-modal__container) {
      flex: none;
      max-width: 342px;
      max-height: calc(100vh - 48px);
      overflow: auto;
      border-radius: ${CARD_RADIUS};
    }
  }
`;

const Card = styled('div')`
  @import 'stylesheets/design_system/breakpoint';

  position: relative;
  overflow: hidden;
  background: ${CARD_BACKGROUND};

  @include breakpoint(medium, large) {
    min-height: 690px;
  }
`;

const PlanetArt = styled('img')`
  @import 'stylesheets/design_system/breakpoint';

  position: absolute;
  z-index: 0;
  pointer-events: none;

  @include breakpoint(medium, large) {
    top: 50%;
    right: -60px;
    height: 115%;
    transform: translateY(-50%);
    object-fit: cover;
  }

  @include breakpoint(small) {
    top: 50%;
    right: -110px;
    height: 105%;
    transform: translateY(-50%);
    object-fit: cover;
  }
`;

const ArtScrim = styled('div')`
  @import 'stylesheets/design_system/breakpoint';

  position: absolute;
  z-index: 1;
  inset: 0;

  @include breakpoint(medium, large) {
    background: linear-gradient(
      100deg,
      rgba(5, 5, 5, 0.97) 0%,
      rgba(5, 5, 5, 0.92) 46%,
      rgba(5, 5, 5, 0.45) 72%,
      rgba(5, 5, 5, 0.05) 100%
    );
  }

  @include breakpoint(small) {
    background: linear-gradient(
      115deg,
      rgba(5, 5, 5, 0.97) 0%,
      rgba(5, 5, 5, 0.92) 52%,
      rgba(5, 5, 5, 0.5) 76%,
      rgba(5, 5, 5, 0.1) 100%
    );
  }
`;

const Content = styled('div')`
  @import 'stylesheets/design_system/breakpoint';

  position: relative;
  z-index: 2;
  display: flex;
  flex-direction: column;

  @include breakpoint(medium, large) {
    justify-content: center;
    min-height: 690px;
    padding: var(--spacing-lg-40, 40px) 82px;
  }

  @include breakpoint(small) {
    min-height: 640px;
    padding: 28px var(--spacing-sm-24, 24px);
  }
`;

const DismissButton = styled('button')`
  position: absolute;
  z-index: 3;
  top: var(--spacing-xs-16, 16px);
  right: var(--spacing-xs-16, 16px);
  display: inline-flex;
  width: 32px;
  height: 32px;
  align-items: center;
  justify-content: center;
  border: 0;
  border-radius: 50%;
  background: rgba(20, 20, 19, 0.45);
  color: var(--text-white, #ffffff);
  cursor: pointer;

  @import 'stylesheets/design_system/breakpoint';

  @include breakpoint(small) {
    top: 14px;
    right: 14px;
    width: 30px;
    height: 30px;
  }
`;

const CloseIcon = styled(Icon)`
  @import 'stylesheets/design_system/breakpoint';

  @include breakpoint(small) {
    transform: scale(0.9);
  }
`;

// "Exact paper match" — the narrow paper/shadow-paper predicate, so the badge
// is a claim about provenance and only ever renders on citations.
const MatchBadge = styled('span')`
  display: inline-flex;
  align-self: flex-start;
  align-items: center;
  gap: var(--spacing-xxs-8, 8px);
  padding: 4px 10px;
  border-radius: var(--border-radius-radius-xs, 4px);
  background: var(--success-light, #f0f8f1);
  color: var(--brand-colors-success-900, #035c0f);
`;

const BadgeText = styled(Body)`
  font-size: 12px;
  line-height: 1.2;
  letter-spacing: 0.02em;
  color: inherit;
  font-weight: var(--type-font-weight-bold, 700);
`;

const Eyebrow = styled(Mono)`
  font-size: 12px;
  line-height: 1.4;
  color: rgba(255, 255, 255, 0.7);
`;

const Headline = styled(Heading)`
  @import 'stylesheets/design_system/breakpoint';
  @import 'stylesheets/design_system/font';
  @include serif;

  margin: 12px 0 0;
  font-size: 32px;
  line-height: 1.15;
  color: var(--text-white, #ffffff);

  @include breakpoint(small) {
    font-size: 27px;
  }
`;

// The cited paper's own title, quoted under the headline on the record layout.
const SubjectLine = styled(Body)`
  @import 'stylesheets/design_system/breakpoint';
  @import 'stylesheets/design_system/font';
  @include serif;

  margin: var(--spacing-xxs-8, 8px) 0 0;
  font-size: 24px;
  line-height: 1.3;
  color: var(--text-white, #ffffff);

  @include breakpoint(small) {
    font-size: 20px;
  }
`;

const SummaryLine = styled(Body)`
  margin: var(--spacing-xxs-8, 8px) 0 0;
  font-size: 16px;
  line-height: 1.5;
  color: rgba(255, 255, 255, 0.8);

  @import 'stylesheets/design_system/breakpoint';

  @include breakpoint(small) {
    font-size: 14px;
  }
`;

// The excerpt sits on a light card: it is a quotation from the citing paper,
// and the designs lift it out of the dark surface rather than tint it.
const ExcerptCard = styled('div')`
  max-width: ${CONTENT_WIDTH};
  margin-top: var(--spacing-xs-16, 16px);
  padding: var(--spacing-xs-16, 16px) 18px;
  border-radius: var(--border-radius-radius-xs, 4px);
  background: var(--background-beige-light, #f6f6f4);
`;

const ExcerptText = styled(Body)`
  @import 'stylesheets/design_system/font';
  @include serif;

  margin: 0;
  font-size: 16px;
  line-height: 1.55;
  color: var(--text-primary, #141413);

  @import 'stylesheets/design_system/breakpoint';

  @include breakpoint(small) {
    font-size: 14px;
  }
`;

const RecordRows = styled('div')`
  display: flex;
  max-width: ${CONTENT_WIDTH};
  flex-direction: column;
  margin-top: var(--spacing-xs-16, 16px);

  @import 'stylesheets/design_system/breakpoint';

  @include breakpoint(small) {
    margin-top: 12px;
  }
  & > *:last-child {
    border-bottom: 1px solid rgba(255, 255, 255, 0.22);
  }
`;

const RecordRow = styled('div')`
  display: flex;
  justify-content: space-between;
  gap: var(--spacing-xs-16, 16px);
  padding: 9px 0;
  border-top: 1px solid rgba(255, 255, 255, 0.22);

  @import 'stylesheets/design_system/breakpoint';

  @include breakpoint(small) {
    padding: 8px 0;
  }
`;

const RowLabel = styled(Mono)`
  font-size: 11px;
  line-height: 1.4;
  color: rgba(255, 255, 255, 0.6);

  @import 'stylesheets/design_system/breakpoint';

  @include breakpoint(small) {
    font-size: 10px;
  }
`;

const RowValue = styled(Body)`
  font-size: 14px;
  line-height: 1.4;
  color: var(--text-white, #ffffff);
  text-align: right;

  @import 'stylesheets/design_system/breakpoint';

  @include breakpoint(small) {
    font-size: 13px;
  }
`;

const Cta = styled(ButtonLink)`
  max-width: ${CONTENT_WIDTH};
  margin-top: var(--spacing-sm-24, 24px);
  border-radius: 16px;
  font-size: 15px;

  @import 'stylesheets/design_system/breakpoint';

  @include breakpoint(small) {
    margin-top: 16px;
  }
`;

const GuaranteeText = styled(Body)`
  max-width: ${CONTENT_WIDTH};
  margin-top: var(--spacing-xxs-8, 8px);
  font-size: 12px;
  line-height: 1.4;
  color: rgba(255, 255, 255, 0.6);
  text-align: center;
`;

const ROW_CITED_IN = 'cited_in';
const ROW_CITING_AUTHOR = 'citing_author';
const ROW_CITING_COUNTRY = 'citing_country';
const ROW_CITING_JOURNAL = 'citing_journal';
const TRACKED_RECORD_ROWS = new Set([
  ROW_CITED_IN,
  ROW_CITING_AUTHOR,
  ROW_CITING_COUNTRY,
  ROW_CITING_JOURNAL,
]);

type Row = { key: string; label: string; value: string };

function citationReferenceText(excerpt: CitationExcerpt): string {
  const lead = [excerpt.authors, excerpt.publicationYear]
    .filter(Boolean)
    .join(' ');
  const issue = excerpt.issue ? `(${excerpt.issue})` : '';
  const volume = [excerpt.volume, issue].filter(Boolean).join('');
  const publication = [excerpt.journalName, volume, excerpt.pages]
    .filter(Boolean)
    .join(', ');

  return [lead, excerpt.title, publication]
    .filter(Boolean)
    .map((fragment) => (fragment.endsWith('.') ? fragment : `${fragment}.`))
    .join(' ');
}

export const SingularMentionCitationModal: React.FC<
  SingularMentionCitationModalProps
> = (props) => {
  const {
    combinedMentionId,
    isCitation,
    layout,
    citingWorkTitle,
    citedPaperTitle,
    matchedName,
    citationExcerpt,
    mentionExcerpt,
    citingAuthorName,
    citingCountryName,
    citingJournalName,
    ctaHref,
    ctaLabel,
    guaranteeText,
  } = props;

  const [open, setOpen] = useState(true);
  const mentionName = mentionExcerpt?.matchedName ?? matchedName;

  const excerptText = useMemo(() => {
    if (layout !== 'excerpt') {
      return null;
    }
    if (isCitation) {
      return citationExcerpt ? citationReferenceText(citationExcerpt) : null;
    }
    return mentionExcerpt?.highlight ?? null;
  }, [layout, isCitation, citationExcerpt, mentionExcerpt]);

  const highlightNeedles = isCitation
    ? [citationExcerpt?.authors, citationExcerpt?.title]
    : [mentionExcerpt?.matchedName ?? matchedName];

  const rows = useMemo<Row[]>(() => {
    const candidates: Row[] = [
      {
        key: ROW_CITED_IN,
        label: isCitation ? 'Cited in' : 'Found in',
        value: citingWorkTitle,
      },
      {
        key: ROW_CITING_AUTHOR,
        label: 'Citing author',
        value: citingAuthorName ?? '',
      },
      {
        key: ROW_CITING_COUNTRY,
        label: 'Country',
        value: citingCountryName ?? '',
      },
      {
        key: ROW_CITING_JOURNAL,
        label: 'Journal',
        value: citingJournalName ?? '',
      },
    ];

    return candidates.filter((row) => row.value.trim().length > 0);
  }, [
    isCitation,
    citingWorkTitle,
    citingAuthorName,
    citingCountryName,
    citingJournalName,
  ]);

  const extra = trackingExtra(
    props,
    rows
      .filter((row) => TRACKED_RECORD_ROWS.has(row.key))
      .map((row) => row.key),
    layout === 'excerpt',
  );

  const handleDismiss = () => {
    setOpen(false);
    trackClick({
      source: SOURCES.dismiss,
      entityId: combinedMentionId,
      extra,
    });
    void recordDismissal(DISMISSIBLE_NAME);
  };

  const handleCtaClick = () => {
    trackClick({
      source: SOURCES.ctaClick,
      entityId: combinedMentionId,
      extra,
    });
    void recordClickthrough(DISMISSIBLE_NAME);
  };

  return (
    <ModalDialog open={open} onClose={handleDismiss} showHeader={false}>
      <ImpressionWatcher
        source={SOURCES.impression}
        entity_id={combinedMentionId}
        entity_type={ImpressionEntity.Mention}
        extra={extra}
      >
        <Card>
          <PlanetArt src={PLANET_IMAGE} alt="" aria-hidden="true" />
          <ArtScrim aria-hidden="true" />
          <DismissButton
            type="button"
            aria-label="Dismiss"
            onClick={handleDismiss}
          >
            <CloseIcon name="close" size={20} />
          </DismissButton>
          <Content>
            {isCitation ? (
              <MatchBadge>
                <Icon name="check_circle" size={15} />
                <BadgeText as="span">Exact paper match</BadgeText>
              </MatchBadge>
            ) : (
              <Eyebrow>New mention</Eyebrow>
            )}
            <Headline>
              {isCitation
                ? 'Your paper was cited'
                : 'Your name appears in new research'}
            </Headline>
            {excerptText === null && isCitation && citedPaperTitle && (
              <SubjectLine>“{citedPaperTitle}”</SubjectLine>
            )}
            {excerptText === null && !isCitation && mentionName && (
              <SummaryLine>
                {mentionName} is discussed in a newly uploaded paper.
              </SummaryLine>
            )}
            {excerptText !== null && (
              <ExcerptCard>
                <ExcerptText>
                  {highlightText(excerptText, highlightNeedles)}
                </ExcerptText>
              </ExcerptCard>
            )}
            {rows.length > 0 && (
              <RecordRows>
                {rows.map((row) => (
                  <RecordRow key={row.key}>
                    <RowLabel>{row.label}</RowLabel>
                    <RowValue>{row.value}</RowValue>
                  </RecordRow>
                ))}
              </RecordRows>
            )}
            <Cta
              href={ctaHref}
              onClick={handleCtaClick}
              fullWidth
              iconRight="arrow_forward"
            >
              {ctaLabel}
            </Cta>
            {guaranteeText && <GuaranteeText>{guaranteeText}</GuaranteeText>}
          </Content>
        </Card>
      </ImpressionWatcher>
    </ModalDialog>
  );
};

export default SingularMentionCitationModal;
